import React, { useCallback, useEffect } from "react";
import AdminLayout from "../../../layouts/AdminLayout";
import Subheader from "../../../components/ui/Subheader";
import { Space } from "antd";
import IconButton from "../../../components/common/IconButton";
import LeftArrowIcon from "../../../assets/icons/LeftArrowIcon";
import { useNavigate, useParams } from "react-router-dom";
import {
  RootState,
  useAppDispatch,
  useAppSelector,
} from "../../../store/store";
import { GetSinglePriceMatrixParams } from "../../../types/redux";
import { getSinglePriceMatrixAsync } from "../../../store/features/NP/payment/paymentAPI";
import { formatDate } from "../../../utils/time";
import { capitalize } from "../../../utils/string";
import Loader from "../../../components/common/Loader";

export default function PriceMatrixDetails() {
  const navigate = useNavigate();
  const { id } = useParams();
  const dispatch = useAppDispatch();
  const { singlePriceMatrix, singlePriceMatrixLoading } = useAppSelector(
    (state: RootState) => state.payment
  );

  const priceMatrixData = useCallback(() => {
    const params: GetSinglePriceMatrixParams = {
      id: id,
    };

    dispatch(
      getSinglePriceMatrixAsync({
        params,
      })
    );
  }, [dispatch, id]);

  useEffect(() => {
    priceMatrixData();
  }, [priceMatrixData]);

  const generalData = [
    {
      label: "Cluster Name:",
      value: singlePriceMatrix?.cluster?.regionName
        ? capitalize(singlePriceMatrix.cluster.regionName)
        : "N/A",
    },
    {
      label: "Zone Name:",
      value: singlePriceMatrix?.zone?.areaName
        ? capitalize(singlePriceMatrix.zone.areaName)
        : "N/A",
    },
    {
      label: "Vendor:",
      value: singlePriceMatrix?.vendor?.firstName || "N/A",
    },
    { label: "Vendor ID:", value: singlePriceMatrix?.vendorId || "N/A" },
    {
      label: "Effective Date:",
      value: singlePriceMatrix?.effectiveDate
        ? formatDate(singlePriceMatrix.effectiveDate)
        : "N/A",
    },
  ];

  const standardData = [
    {
      label: "Standard_Missed Call Std_Line Item # 1:",
      value: singlePriceMatrix?.standardMissedCallLineItem || "N/A",
    },
    {
      label: "Standard_Missed Call Std_Missed Call Amount:",
      value: singlePriceMatrix?.standardMissedCallAmount ?? "0",
    },
    {
      label: "Standard_Valid TT_Single PG Std_Line Item # 2:",
      value: singlePriceMatrix?.standardValidTTSinglePGLineItem || "N/A",
    },
    {
      label: "Standard_Valid TT_Single PG Std_Valid TT Amount:",
      value: singlePriceMatrix?.standardValidTTSinglePGAmount ?? "0",
    },
    {
      label: "Standard_Valid TT_Double PG Std_Line Item # 3:",
      value: singlePriceMatrix?.standardValidTTDoublePGLineItem || "N/A",
    },
    {
      label: "Standard_Valid TT_Double PG Std_Valid TT Amount:",
      value: singlePriceMatrix?.standardValidTTDoublePGAmount ?? "0",
    },
    {
      label: "Standard_PGRH Std_Line Item # 4:",
      value: singlePriceMatrix?.standardPGRHLineItem || "N/A",
    },
    {
      label: "Standard_PGRH Std_PGRH Amount:",
      value: singlePriceMatrix?.standardPGRHAmount ?? "0",
    },
  ];

  const premiumData = [
    {
      label: "Premium_Missed Call Prm_Line Item # 1:",
      value: singlePriceMatrix?.premiumMissedCallLineItem || "N/A",
    },
    {
      label: "Premium_Missed Call Prm_Missed Call Amount:",
      value: singlePriceMatrix?.premiumMissedCallAmount ?? "0",
    },
    {
      label: "Premium_Valid TT_Single PG Prm_Line Item # 2:",
      value: singlePriceMatrix?.premiumValidTTSinglePGLineItem || "N/A",
    },
    {
      label: "Premium_Valid TT_Single PG Prm_Valid TT Amount:",
      value: singlePriceMatrix?.premiumValidTTSinglePGAmount ?? "0",
    },
    {
      label: "Premium_Valid TT_Double PG Prm_Line Item # 3:",
      value: singlePriceMatrix?.premiumValidTTDoublePGLineItem || "N/A",
    },
    {
      label: "Premium_Valid TT_Double PG Prm_Valid TT Amount:",
      value: singlePriceMatrix?.premiumValidTTDoublePGAmount ?? "0",
    },
    {
      label: "Premium_PGRH Prm_Line Item # 4:",
      value: singlePriceMatrix?.premiumPGRHLineItem || "N/A",
    },
    {
      label: "Premium_PGRH Prm_PGRH Amount:",
      value: singlePriceMatrix?.premiumPGRHAmount ?? "0",
    },
  ];

  const contingencyData = [
    {
      label: "Contingency_Missed Call Con_Line Item # 1:",
      value: singlePriceMatrix?.contingencyMissedCallLineItem || "N/A",
    },
    {
      label: "Contingency_Missed Call Con_Missed Call Amount:",
      value: singlePriceMatrix?.contingencyMissedCallAmount ?? "0",
    },
    {
      label: "Contingency_Valid TT_Single PG Con_Line Item # 2:",
      value: singlePriceMatrix?.contingencyValidTTSinglePGLineItem || "N/A",
    },
    {
      label: "Contingency_Valid TT_Single PG Con_Valid TT Amount:",
      value: singlePriceMatrix?.contingencyValidTTSinglePGAmount ?? "0",
    },
    {
      label: "Contingency_Valid TT_Double PG Con_Line Item # 3:",
      value: singlePriceMatrix?.contingencyValidTTDoublePGLineItem || "N/A",
    },
    {
      label: "Contingency_Valid TT_Double PG Con_Valid TT Amount:",
      value: singlePriceMatrix?.contingencyValidTTDoublePGAmount ?? "0",
    },
    {
      label: "Contingency_PGRH Con_Line Item # 4:",
      value: singlePriceMatrix?.contingencyPGRHLineItem || "N/A",
    },
    {
      label: "Contingency_PGRH Con_PGRH Amount:",
      value: singlePriceMatrix?.contingencyPGRHAmount ?? "0",
    },
  ];

  return (
    <>
      <AdminLayout title="Price Matrix" headerTitle="Price Matrix">
        {singlePriceMatrixLoading ? (
          <Loader />
        ) : (
          <section className={`shadow-sm bg-white rounded-lg mt-7 pb-7`}>
            <Subheader title="Price Matrix Details" subTitle={``}>
              <Space size="middle">
                {" "}
                <IconButton
                  className="!border-others-header !text-sm !font-normal !rounded-lg !border !text-header !bg-transparent"
                  type="default"
                  icon={<LeftArrowIcon />}
                  onClick={() => navigate(-1)}
                >
                  <span>Back</span>
                </IconButton>
              </Space>
            </Subheader>

            <div className="max-w-2xl mx-auto px-6 pt-14 min-h-[400px]">
              <div className="flex items-center gap-4 mb-5 mt-5">
                <div className="text-slate-600 text-lg font-semibold">
                  General Information
                </div>
                <div className="flex-1 h-px bg-zinc-200"></div>
              </div>

              <div>
                {generalData.map((item, index) => (
                  <div key={index}>
                    <div className="flex justify-between items-center w-full mb-2 mt-2">
                      <div className="text-slate-400 text-base font-medium">
                        {item.label}
                      </div>
                      <div className="text-slate-600 text-base font-medium text-right">
                        {item.value}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex items-center gap-4 mb-5 mt-5">
                <div className="text-slate-600 text-lg font-semibold">
                  Standard Information
                </div>
                <div className="flex-1 h-px bg-zinc-200"></div>
              </div>

              <div>
                {standardData.map((item, index) => (
                  <div key={index}>
                    <div className="flex justify-between items-center w-full mb-2 mt-2">
                      <div className="text-slate-400 text-base font-medium">
                        {item.label}
                      </div>
                      <div
                        className={`text-base font-medium text-right text-slate-600 `}
                      >
                        {item.value}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex items-center gap-4 mb-5 mt-5">
                <div className="text-slate-600 text-lg font-semibold">
                  Premium Information
                </div>
                <div className="flex-1 h-px bg-zinc-200"></div>
              </div>

              <div>
                {premiumData.map((item, index) => (
                  <div key={index}>
                    <div className="flex justify-between items-center w-full mb-2 mt-2">
                      <div className="text-slate-400 text-base font-medium">
                        {item.label}
                      </div>
                      <div className="text-slate-600 text-base font-medium text-right">
                        {item.value}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex items-center gap-4 mb-5 mt-5">
                <div className="text-slate-600 text-lg font-semibold">
                  Contingency Information
                </div>
                <div className="flex-1 h-px bg-zinc-200"></div>
              </div>

              <div>
                {contingencyData.map((item, index) => (
                  <div key={index}>
                    <div className="flex justify-between items-center w-full mb-2 mt-2">
                      <div className="text-slate-400 text-base font-medium">
                        {item.label}
                      </div>
                      <div className="text-slate-600 text-base font-medium text-right">
                        {item.value}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}
      </AdminLayout>
    </>
  );
}
